import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Download, Loader2, Upload } from 'lucide-react';

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // attachment; filename="foo.xlsx"
  const m = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(header);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Let the browser start the download first.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

async function getErrorMessage(resp: Response, fallback: string): Promise<string> {
  const text = await resp.text().catch(() => '');
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text) as any;
    if (typeof parsed?.detail === 'string') return parsed.detail;
    if (Array.isArray(parsed?.detail) && parsed.detail.length > 0) return String(parsed.detail[0]);
    if (parsed && typeof parsed === 'object') return JSON.stringify(parsed);
    return text;
  } catch {
    return text;
  }
}

type ProdBrand = {
  id: number;
  brand_name: string;
  favourite: boolean;
};

type QuickDataInputUploadResult = {
  template_version: string;
  free_gift_cost: {
    inserted_count: number;
    updated_count: number;
    unchanged_count: number;
    processed_count: number;
    skipped_rows: number;
    inserted_skus: string[];
    updated_skus: string[];
    unchanged_skus: string[];
  };
  special_calculation_groups: {
    updated_count: number;
    unchanged_count: number;
    processed_sheet_count: number;
    skipped_rows: number;
    updated_groups: string[];
    unchanged_groups: string[];
  };
};

const HIDDEN_BRAND_NAMES = new Set(['Uncategorized', 'FREE GIFT', 'GIFT CARD']);

export function ReportPage() {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const [brands, setBrands] = useState<ProdBrand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandsError, setBrandsError] = useState<string | null>(null);
  const [updatingBrandIds, setUpdatingBrandIds] = useState<Set<number>>(new Set());

  const [templateStartDate, setTemplateStartDate] = useState<string>('');
  const [templateEndDate, setTemplateEndDate] = useState<string>('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<QuickDataInputUploadResult | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const canGenerate = useMemo(() => !!startDate && !!endDate, [startDate, endDate]);
  const canDownloadTemplate = useMemo(
    () => !!templateStartDate && !!templateEndDate,
    [templateStartDate, templateEndDate]
  );
  const visibleBrands = useMemo(() => brands.filter((b) => !HIDDEN_BRAND_NAMES.has(b.brand_name)), [brands]);
  const favouriteCount = useMemo(() => visibleBrands.filter((b) => !!b.favourite).length, [visibleBrands]);
  const brandsSorted = useMemo(() => {
    // Small UX improvement: favourites first, then alphabetical.
    return visibleBrands
      .slice()
      .sort((a, b) =>
        a.favourite === b.favourite
          ? a.brand_name.localeCompare(b.brand_name)
          : a.favourite
            ? -1
            : 1
      );
  }, [visibleBrands]);

  async function downloadGpReportXlsx(params: { startDate: string; endDate: string }) {
    const resp = await fetch('/v1/api/gp-reporter/generate-gp-report/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ start_date: params.startDate, end_date: params.endDate }),
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
    }

    const contentDisposition = resp.headers.get('content-disposition');
    const fallbackName = `gp_report_${params.startDate}_to_${params.endDate}.xlsx`;
    const filename = parseFilenameFromContentDisposition(contentDisposition) ?? fallbackName;
    const blob = await resp.blob();
    triggerBrowserDownload(blob, filename);
    return { filename };
  }

  async function downloadQuickDataInputTemplate(params: { startDate: string; endDate: string }) {
    const resp = await fetch('/v1/api/gp-reporter/quick-data-input/template/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ start_date: params.startDate, end_date: params.endDate }),
    });

    if (!resp.ok) {
      throw new Error(await getErrorMessage(resp, `Request failed (${resp.status}).`));
    }

    const contentDisposition = resp.headers.get('content-disposition');
    const fallbackName = `quick_data_input_${params.startDate}_${params.endDate}.xlsx`;
    const filename = parseFilenameFromContentDisposition(contentDisposition) ?? fallbackName;
    const blob = await resp.blob();
    triggerBrowserDownload(blob, filename);
    return { filename };
  }

  async function handleGenerate() {
    if (!canGenerate || reportLoading) return;
    setReportLoading(true);
    setReportError(null);
    try {
      await downloadGpReportXlsx({ startDate, endDate });
    } catch (e) {
      setReportError(e instanceof Error ? e.message : 'Failed to generate report.');
    } finally {
      setReportLoading(false);
    }
  }

  async function handleTemplateDownload() {
    if (!canDownloadTemplate || templateLoading) return;
    setTemplateLoading(true);
    setTemplateError(null);
    try {
      await downloadQuickDataInputTemplate({ startDate: templateStartDate, endDate: templateEndDate });
    } catch (e) {
      setTemplateError(e instanceof Error ? e.message : 'Failed to download template.');
    } finally {
      setTemplateLoading(false);
    }
  }

  async function handleWorkbookUpload() {
    if (!uploadFile || uploadLoading) return;
    setUploadLoading(true);
    setUploadError(null);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const resp = await fetch('/v1/api/gp-reporter/quick-data-input/upload/', {
        method: 'POST',
        body: formData,
      });
      if (!resp.ok) {
        throw new Error(await getErrorMessage(resp, `Request failed (${resp.status}).`));
      }
      const result = (await resp.json()) as QuickDataInputUploadResult;
      setUploadResult(result);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Failed to upload workbook.');
    } finally {
      setUploadLoading(false);
    }
  }

  async function loadBrands() {
    setBrandsLoading(true);
    setBrandsError(null);
    try {
      const resp = await fetch('/v1/api/gp-reporter/brands/', { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const data = (await resp.json()) as unknown;
      const rows = Array.isArray(data) ? data : [];
      const parsed: ProdBrand[] = rows
        .map((r) => {
          const id = Number((r as any)?.id);
          const brand_name = String((r as any)?.brand_name ?? '').trim();
          const favourite = Boolean((r as any)?.favourite);
          if (!Number.isFinite(id) || !brand_name) return null;
          return { id, brand_name, favourite };
        })
        .filter(Boolean) as ProdBrand[];
      setBrands(parsed);
    } catch (e) {
      setBrandsError(e instanceof Error ? e.message : 'Failed to load brands.');
    } finally {
      setBrandsLoading(false);
    }
  }

  async function setBrandFavourite(params: { brandId: number; favourite: boolean }) {
    const { brandId, favourite } = params;
    const prevFavourite = brands.find((b) => b.id === brandId)?.favourite;
    setUpdatingBrandIds((s) => new Set([...Array.from(s), brandId]));
    setBrands((current) => current.map((b) => (b.id === brandId ? { ...b, favourite } : b)));

    try {
      const resp = await fetch(`/v1/api/gp-reporter/brands/${brandId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ favourite }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
    } catch (e) {
      if (typeof prevFavourite === 'boolean') {
        setBrands((current) => current.map((b) => (b.id === brandId ? { ...b, favourite: prevFavourite } : b)));
      }
      setBrandsError(e instanceof Error ? e.message : 'Failed to update favourite.');
    } finally {
      setUpdatingBrandIds((s) => {
        const next = new Set(s);
        next.delete(brandId);
        return next;
      });
    }
  }

  useEffect(() => {
    void loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generate Sales Report</CardTitle>
          <CardDescription>
            Select date range to generate an Excel report (favourite brands)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <DateInput label="Start Date" value={startDate} onChange={(e) => setStartDate(e.currentTarget.value)} />
            <DateInput label="End Date" value={endDate} onChange={(e) => setEndDate(e.currentTarget.value)} />
          </div>
          <Button onClick={handleGenerate} disabled={!canGenerate || reportLoading} className="mt-2">
            {reportLoading ? 'Generating...' : 'Create Report'}
          </Button>
          {reportError && (
            <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span className="break-words">{reportError}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick Favourite</CardTitle>
          <CardDescription>
            Tap the brand to select as favourite.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              {brandsLoading ? 'Loading brands...' : `${favouriteCount} favourite / ${visibleBrands.length} total`}
            </div>
            <Button variant="outline" size="sm" onClick={loadBrands} disabled={brandsLoading}>
              Refresh
            </Button>
          </div>

          {brandsError && (
            <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span className="break-words">{brandsError}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {brandsSorted.map((b) => {
              const active = !!b.favourite;
              const updating = updatingBrandIds.has(b.id);
              return (
                <Button
                  key={b.id}
                  type="button"
                  aria-pressed={active}
                  disabled={brandsLoading || updating}
                  onClick={() => setBrandFavourite({ brandId: b.id, favourite: !active })}
                  variant={active ? 'secondary' : 'outline'}
                  size="sm"
                  className="rounded-md h-8 px-3 text-xs font-normal whitespace-nowrap"
                  title={b.brand_name}
                >
                  <span className="max-w-[280px] truncate">{b.brand_name}</span>
                  {updating && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick Data Input</CardTitle>
          <CardDescription>
            Download a structured workbook, update it offline, then upload it to apply free gift cost and favourite special group changes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border p-4 space-y-4">
            <div>
              <div className="font-medium">Template Download</div>
              <div className="text-sm text-muted-foreground">
                Generate a workbook for the selected period with free gift SKUs and favourite special calculation group sheets.
              </div>
            </div>
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              <DateInput
                label="Start Date"
                value={templateStartDate}
                onChange={(e) => setTemplateStartDate(e.currentTarget.value)}
              />
              <DateInput
                label="End Date"
                value={templateEndDate}
                onChange={(e) => setTemplateEndDate(e.currentTarget.value)}
              />
            </div>
            <Button onClick={handleTemplateDownload} disabled={!canDownloadTemplate || templateLoading}>
              {templateLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Download Template
                </>
              )}
            </Button>
            {templateError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Template Download Failed</AlertTitle>
                <AlertDescription>{templateError}</AlertDescription>
              </Alert>
            )}
          </div>

          <div className="rounded-lg border p-4 space-y-4">
            <div>
              <div className="font-medium">Upload & Processing</div>
              <div className="text-sm text-muted-foreground">
                Upload the completed workbook. Blank template rows remain unchanged, while populated rows are validated and applied.
              </div>
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <Input
                ref={uploadInputRef}
                type="file"
                accept=".xlsx"
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0] ?? null;
                  setUploadFile(file);
                  setUploadError(null);
                }}
              />
              <Button onClick={handleWorkbookUpload} disabled={!uploadFile || uploadLoading}>
                {uploadLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Workbook
                  </>
                )}
              </Button>
            </div>

            {uploadError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Upload Failed</AlertTitle>
                <AlertDescription>{uploadError}</AlertDescription>
              </Alert>
            )}

            {uploadResult && (
              <Alert>
                <Upload className="h-4 w-4" />
                <AlertTitle>Upload Processed</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>Template version: {uploadResult.template_version}</p>
                  <p>
                    Free gift cost: {uploadResult.free_gift_cost.inserted_count} inserted, {uploadResult.free_gift_cost.updated_count} updated, {uploadResult.free_gift_cost.unchanged_count} unchanged.
                  </p>
                  <p>
                    Special calculation groups: {uploadResult.special_calculation_groups.updated_count} updated, {uploadResult.special_calculation_groups.unchanged_count} unchanged.
                  </p>
                  <p>
                    Skipped blank rows: {uploadResult.free_gift_cost.skipped_rows + uploadResult.special_calculation_groups.skipped_rows}
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
